import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import ImageUploader from "../../components/ImageUploader";
import { Plus, BadgeCheck, Edit2, Archive, RotateCcw, Search, X, Trash2, Copy, Check, Code2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "archived", label: "Archived" },
];

const EMPTY_BRAND = {
    name: "",
    tagline: "",
    description: "",
    logo_url: "",
    hero_image_url: "",
    website: "",
    category: "outdoor-gear",
    location: "",
    founded_year: "",
    instagram: "",
    youtube: "",
    gear_highlights: [],
    status: "active",
};

export default function BrandsTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState(null); // { mode:'create'|'edit', brand }
    const [selected, setSelected] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null); // { brand }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/brands", {
                params: { q: q.trim(), status, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, status, page]);

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    useEffect(() => {
        setPage(1);
    }, [q, status]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((b) => b.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const onSave = async (form) => {
        try {
            if (editor.mode === "create") {
                await api.post("/admin/brands", form);
                toast.success(`Brand "${form.name}" created`);
            } else {
                await api.patch(`/admin/brands/${editor.brand.id}`, form);
                toast.success("Brand updated");
            }
            setEditor(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onArchive = async (brand) => {
        try {
            await api.delete(`/admin/brands/${brand.id}`);
            toast.success(`${brand.name} archived`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onRestore = async (brand) => {
        try {
            await api.patch(`/admin/brands/${brand.id}`, { status: "active" });
            toast.success(`${brand.name} restored`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onHardDelete = async () => {
        if (!pendingDelete) return;
        try {
            await api.delete(`/admin/brands/${pendingDelete.brand.id}?hard=true`);
            toast.success(`${pendingDelete.brand.name} permanently deleted`);
            setPendingDelete(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const runBulk = async (action, reason) => {
        try {
            const { data } = await api.post(`/admin/brands/bulk?action=${action}`, {
                ids: selected,
                reason,
            });
            toast.success(`${data.affected} brand(s) ${action}d`);
            setBulkConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((b) => selected.includes(b.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "name", label: "Name" },
                { key: "category", label: "Category" },
                { key: "website", label: "Website" },
                { key: "status", label: "Status" },
                { key: "campaigns_active", label: "Active Campaigns" },
                { key: "campaigns_total", label: "Total Campaigns" },
                { key: "partner_since", label: "Partner Since" },
            ],
            "brands"
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <BadgeCheck className="w-5 h-5 text-ember-orange" />
                    <h3 className="font-display font-bold text-lg text-midnight-pine">Brand partners</h3>
                </div>
                <button
                    data-testid="brands-new-btn"
                    onClick={() => setEditor({ mode: "create", brand: EMPTY_BRAND })}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark active:scale-95 transition-all"
                >
                    <Plus className="w-4 h-4" /> New brand
                </button>
            </div>

            <div className="bg-snowcap border border-ridge-line rounded-lg p-4 space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                    <input
                        data-testid="brands-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search brand by name or ID…"
                        className="w-full pl-10 pr-4 py-2.5 rounded-md bg-alpine-mist border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-sm"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                        <button
                            key={s.id}
                            data-testid={`brands-status-${s.id}`}
                            onClick={() => setStatus(s.id)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${
                                status === s.id ? "bg-midnight-pine text-white" : "bg-alpine-mist text-slate-gray hover:text-granite"
                            }`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="brands-bulk"
                    actions={[
                        { id: "archive", label: "Archive", onClick: () => setBulkConfirm({ action: "archive", title: `Archive ${selected.length} brand(s)?`, requireReason: false }) },
                        { id: "restore", label: "Restore", onClick: () => setBulkConfirm({ action: "restore", title: `Restore ${selected.length} brand(s)?`, requireReason: false }) },
                        { id: "delete", label: "Delete forever", danger: true, onClick: () => setBulkConfirm({ action: "delete", danger: true, title: `Permanently delete ${selected.length} brand(s)?`, requireReason: true, description: "Irreversible. Also deletes every ad campaign attached to these brands." }) },
                    ]}
                />
            )}

            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 pl-4 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="brands-select-all-cb"
                                        checked={items.length > 0 && selected.length === items.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 px-4">Brand</th>
                                <th className="py-3 px-4">Category</th>
                                <th className="py-3 px-4">Campaigns</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="py-10 text-center text-slate-gray">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="py-12 text-center text-slate-gray">
                                    No brands yet. Click <span className="font-bold text-granite">New brand</span> to add one.
                                </td></tr>
                            ) : (
                                items.map((b) => {
                                    const archived = b.status === "archived";
                                    return (
                                        <tr
                                            key={b.id}
                                            data-testid={`brand-row-${b.id}`}
                                            className={`border-t border-ridge-line ${archived ? "opacity-60" : ""}`}
                                        >
                                            <td className="py-3 pl-4">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`brand-cb-${b.id}`}
                                                    checked={selected.includes(b.id)}
                                                    onChange={() => toggleOne(b.id)}
                                                />
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-3">
                                                    {b.logo_url ? (
                                                        <img
                                                            src={b.logo_url}
                                                            alt=""
                                                            className="w-10 h-10 rounded-md object-cover bg-alpine-mist shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-md bg-alpine-mist border border-ridge-line shrink-0" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="font-bold text-granite truncate">{b.name}</div>
                                                        {b.website && (
                                                            <a
                                                                href={b.website}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs text-slate-gray hover:text-ember-orange truncate block"
                                                            >
                                                                {b.website.replace(/^https?:\/\//, "")}
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-xs text-slate-gray capitalize">{b.category?.replace(/-/g, " ")}</td>
                                            <td className="py-3 px-4 text-xs text-slate-gray">
                                                <span className="text-granite font-bold">{b.campaigns_active}</span> active · {b.campaigns_total} total
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                                                    archived ? "bg-slate-gray/20 text-slate-gray" : "bg-deep-evergreen/15 text-deep-evergreen"
                                                }`}>
                                                    {b.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        data-testid={`brand-edit-${b.id}`}
                                                        onClick={() => setEditor({ mode: "edit", brand: b })}
                                                        className="p-1.5 rounded hover:bg-alpine-mist"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5 text-slate-gray" />
                                                    </button>
                                                    {archived ? (
                                                        <button
                                                            data-testid={`brand-restore-${b.id}`}
                                                            onClick={() => onRestore(b)}
                                                            className="p-1.5 rounded hover:bg-alpine-mist"
                                                            title="Restore"
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5 text-deep-evergreen" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            data-testid={`brand-archive-${b.id}`}
                                                            onClick={() => onArchive(b)}
                                                            className="p-1.5 rounded hover:bg-alpine-mist"
                                                            title="Archive"
                                                        >
                                                            <Archive className="w-3.5 h-3.5 text-slate-gray" />
                                                        </button>
                                                    )}
                                                    <button
                                                        data-testid={`brand-delete-${b.id}`}
                                                        onClick={() => setPendingDelete({ brand: b })}
                                                        className="p-1.5 rounded hover:bg-ember-orange/10"
                                                        title="Delete forever"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5 text-ember-orange" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between p-4 border-t border-ridge-line">
                    <span className="text-xs text-slate-gray font-semibold">
                        Showing {items.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + items.length} of {total}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="brands-prev"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40"
                        >Prev</button>
                        <span className="text-xs font-bold text-granite">{page} / {totalPages}</span>
                        <button
                            data-testid="brands-next"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40"
                        >Next</button>
                    </div>
                </div>
            </div>

            {editor && <BrandEditor mode={editor.mode} brand={editor.brand} onClose={() => setEditor(null)} onSave={onSave} />}

            <AdminConfirmDialog
                open={!!bulkConfirm}
                onClose={() => setBulkConfirm(null)}
                onConfirm={(reason) => runBulk(bulkConfirm.action, reason)}
                title={bulkConfirm?.title || ""}
                description={bulkConfirm?.description || "Logged to the audit trail."}
                confirmLabel="Apply"
                danger={bulkConfirm?.danger}
                requireReason={bulkConfirm?.requireReason}
            />

            <AdminConfirmDialog
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={onHardDelete}
                title={`Permanently delete "${pendingDelete?.brand?.name}"?`}
                description="This cannot be undone. All ad campaigns attached to this brand will also be deleted."
                confirmLabel="Delete forever"
                danger
            />
        </div>
    );
}

function BrandEditor({ mode, brand, onClose, onSave }) {
    const [form, setForm] = useState({
        name: brand.name || "",
        tagline: brand.tagline || "",
        description: brand.description || "",
        logo_url: brand.logo_url || "",
        hero_image_url: brand.hero_image_url || "",
        website: brand.website || "",
        category: brand.category || "outdoor-gear",
        location: brand.location || "",
        founded_year: brand.founded_year || "",
        instagram: brand.instagram || "",
        youtube: brand.youtube || "",
        gear_highlights: brand.gear_highlights || [],
        status: brand.status || "active",
    });
    const [embedTheme, setEmbedTheme] = useState("light");
    const [copied, setCopied] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || form.name.trim().length < 2) {
            toast.error("Brand name must be at least 2 characters");
            return;
        }
        onSave(form);
    };

    const embedSrc = `${API_BASE}/api/brands/${brand.id}/embed.html?theme=${embedTheme}`;
    const embedSnippet = `<iframe src="${embedSrc}" width="320" height="120" style="border:0;overflow:hidden" loading="lazy" title="Featured on RealWorld Outdoors"></iframe>`;
    const copyEmbed = async () => {
        try {
            await navigator.clipboard.writeText(embedSnippet);
            setCopied(true);
            toast.success("Embed code copied");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Couldn't copy");
        }
    };

    return (
        <div
            data-testid="brand-editor"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-midnight-pine/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-snowcap rounded-lg shadow-2xl border border-ridge-line w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col"
            >
                <div className="flex items-center justify-between p-5 border-b border-ridge-line">
                    <h3 className="font-display font-bold text-xl text-midnight-pine">
                        {mode === "create" ? "Create new brand" : `Edit ${brand.name}`}
                    </h3>
                    <button type="button" onClick={onClose} className="text-slate-gray hover:text-granite p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto flex-1">
                    <p className="text-xs text-slate-gray">
                        Everything here is what members see on the public brand page. Fill it out to make a rich profile.
                    </p>
                    <Field label="Name" required>
                        <input
                            data-testid="brand-input-name"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            className={inputCls}
                            placeholder="e.g. REI Co-op"
                        />
                    </Field>
                    <Field label="Tagline">
                        <input
                            data-testid="brand-input-tagline"
                            value={form.tagline}
                            onChange={(e) => update("tagline", e.target.value)}
                            className={inputCls}
                            maxLength={140}
                            placeholder="One line that sums up the brand"
                        />
                    </Field>
                    <Field label="About / story">
                        <textarea
                            data-testid="brand-input-description"
                            rows={4}
                            value={form.description}
                            onChange={(e) => update("description", e.target.value)}
                            className={inputCls}
                            maxLength={2000}
                            placeholder="The brand's story — who they are, what they make, what they stand for…"
                        />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Logo">
                            <ImageUploader
                                value={form.logo_url}
                                onChange={(url) => update("logo_url", url)}
                                aspect="aspect-square"
                                testid="brand-input-logo"
                                allowPaste
                                fallback={<BadgeCheck className="w-6 h-6 text-slate-gray" />}
                            />
                        </Field>
                        <Field label="Hero / banner image">
                            <ImageUploader
                                value={form.hero_image_url}
                                onChange={(url) => update("hero_image_url", url)}
                                aspect="aspect-video"
                                testid="brand-input-hero"
                                allowPaste
                                fallback={<BadgeCheck className="w-6 h-6 text-slate-gray" />}
                            />
                        </Field>
                        <Field label="Website">
                            <input
                                data-testid="brand-input-website"
                                value={form.website}
                                onChange={(e) => update("website", e.target.value)}
                                className={inputCls}
                                placeholder="https://example.com"
                            />
                        </Field>
                        <Field label="Category">
                            <select
                                data-testid="brand-input-category"
                                value={form.category}
                                onChange={(e) => update("category", e.target.value)}
                                className={inputCls}
                            >
                                <option value="outdoor-gear">Outdoor Gear</option>
                                <option value="apparel">Apparel</option>
                                <option value="footwear">Footwear</option>
                                <option value="nutrition">Nutrition</option>
                                <option value="electronics">Electronics</option>
                                <option value="travel">Travel</option>
                                <option value="other">Other</option>
                            </select>
                        </Field>
                        <Field label="Location">
                            <input
                                data-testid="brand-input-location"
                                value={form.location}
                                onChange={(e) => update("location", e.target.value)}
                                className={inputCls}
                                placeholder="Boulder, Colorado"
                            />
                        </Field>
                        <Field label="Founded year">
                            <input
                                data-testid="brand-input-founded"
                                value={form.founded_year}
                                onChange={(e) => update("founded_year", e.target.value)}
                                className={inputCls}
                                maxLength={10}
                                placeholder="1938"
                            />
                        </Field>
                        <Field label="Instagram URL">
                            <input
                                data-testid="brand-input-instagram"
                                value={form.instagram}
                                onChange={(e) => update("instagram", e.target.value)}
                                className={inputCls}
                                placeholder="https://instagram.com/brand"
                            />
                        </Field>
                        <Field label="YouTube URL">
                            <input
                                data-testid="brand-input-youtube"
                                value={form.youtube}
                                onChange={(e) => update("youtube", e.target.value)}
                                className={inputCls}
                                placeholder="https://youtube.com/@brand"
                            />
                        </Field>
                    </div>
                    <Field label="What they're known for">
                        <input
                            data-testid="brand-input-gear"
                            value={(form.gear_highlights || []).join(", ")}
                            onChange={(e) => update("gear_highlights", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                            className={inputCls}
                            placeholder="Tents, Sleeping bags, Trekking poles  (comma-separated)"
                        />
                    </Field>
                    <Field label="Status">
                        <select
                            data-testid="brand-input-status"
                            value={form.status}
                            onChange={(e) => update("status", e.target.value)}
                            className={inputCls}
                        >
                            <option value="active">Active</option>
                            <option value="archived">Archived</option>
                        </select>
                    </Field>

                    {/* Partner "Featured on RealWorld Outdoors" badge — admin tool */}
                    {mode === "edit" && (
                        <div data-testid="brand-embed-tool" className="rounded-lg border border-ridge-line bg-alpine-mist/50 p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Code2 className="w-4 h-4 text-slate-gray" />
                                <h4 className="font-display font-bold text-sm text-midnight-pine">Partner badge embed</h4>
                            </div>
                            <p className="text-xs text-slate-gray mb-3">
                                Give this snippet to the brand to display a “Featured on RealWorld Outdoors” badge on their own site.
                            </p>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-gray">Theme</span>
                                {["light", "dark"].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        data-testid={`brand-embed-theme-${t}`}
                                        onClick={() => setEmbedTheme(t)}
                                        className={`px-2.5 py-1 rounded text-xs font-bold capitalize transition-all ${
                                            embedTheme === t ? "bg-midnight-pine text-white" : "bg-snowcap text-slate-gray border border-ridge-line"
                                        }`}
                                    >{t}</button>
                                ))}
                            </div>
                            <div className="rounded-md overflow-hidden border border-ridge-line bg-snowcap mb-3">
                                <iframe
                                    data-testid="brand-embed-preview"
                                    title="Badge preview"
                                    src={embedSrc}
                                    width="320"
                                    height="120"
                                    style={{ border: 0, maxWidth: "100%" }}
                                    loading="lazy"
                                />
                            </div>
                            <div className="relative">
                                <textarea
                                    readOnly
                                    data-testid="brand-embed-code"
                                    value={embedSnippet}
                                    rows={3}
                                    onClick={(e) => e.target.select()}
                                    className={`${inputCls} font-mono text-xs pr-10`}
                                />
                                <button
                                    type="button"
                                    data-testid="brand-embed-copy"
                                    onClick={copyEmbed}
                                    className="absolute top-2 right-2 p-1.5 rounded-md bg-midnight-pine text-white hover:bg-deep-evergreen"
                                    aria-label="Copy embed code"
                                >
                                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-ridge-line bg-alpine-mist">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md text-sm font-bold text-slate-gray hover:text-granite"
                    >Cancel</button>
                    <button
                        type="submit"
                        data-testid="brand-save"
                        className="px-5 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark"
                    >
                        {mode === "create" ? "Create brand" : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 rounded-md bg-snowcap border border-ridge-line text-sm focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1.5">
                {label}{required && <span className="text-ember-orange ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}
